/**
 * Reusable component for scorecard metadata fields.
 *
 * Used in the upload modal step 2 for reviewing/editing scoring details
 * before upload. Fields are pre-filled from user defaults.
 */
import {
  VIEWING_METHOD_OPTIONS,
  SCORING_TOOL_OPTIONS,
  GAME_PREP_OPTIONS,
  SCORING_STYLE_OPTIONS,
  SCOREBOOK_TYPE_OPTIONS,
  PURCHASED_SCOREBOOK_OPTIONS,
} from '../../hooks/useScoringDefaults';

export interface ScorecardMetadataValues {
  viewingMethod: string | null;
  scoringTool: string | null;
  gamePrep: string | null;
  scoringStyle: string | null;
  scorebookType: string | null;
  scorebookSubtype: string | null;
}

interface ScorecardMetadataFieldsProps {
  /** Current values for all metadata fields */
  values: ScorecardMetadataValues;
  /** Callback when a field value changes */
  onChange: (field: keyof ScorecardMetadataValues, value: string | null) => void;
}

export function ScorecardMetadataFields({ values, onChange }: ScorecardMetadataFieldsProps) {
  const selectClassName =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white';
  const labelClassName = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="space-y-4">
      {/* Row 1: Viewing Method + Scoring Tool */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="viewing-method" className={labelClassName}>
            How did you watch?
          </label>
          <select
            id="viewing-method"
            value={values.viewingMethod || ''}
            onChange={(e) => {
              const newValue = e.target.value || null;
              onChange('viewingMethod', newValue);
            }}
            className={selectClassName}
          >
            <option value="">Not specified</option>
            {VIEWING_METHOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="scoring-tool" className={labelClassName}>
            What did you score with?
          </label>
          <select
            id="scoring-tool"
            value={values.scoringTool || ''}
            onChange={(e) => onChange('scoringTool', e.target.value || null)}
            className={selectClassName}
          >
            <option value="">Not specified</option>
            {SCORING_TOOL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 2: Game Prep + Scoring Style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="game-prep" className={labelClassName}>
            Game prep level
          </label>
          <select
            id="game-prep"
            value={values.gamePrep || ''}
            onChange={(e) => onChange('gamePrep', e.target.value || null)}
            className={selectClassName}
          >
            <option value="">Not specified</option>
            {GAME_PREP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="scoring-style" className={labelClassName}>
            Scoring detail level
          </label>
          <select
            id="scoring-style"
            value={values.scoringStyle || ''}
            onChange={(e) => onChange('scoringStyle', e.target.value || null)}
            className={selectClassName}
          >
            <option value="">Not specified</option>
            {SCORING_STYLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 3: Scorebook Type + Subtype/Details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="scorebook-type" className={labelClassName}>
            Scorebook type
          </label>
          <select
            id="scorebook-type"
            value={values.scorebookType || ''}
            onChange={(e) => {
              const newValue = e.target.value || null;
              onChange('scorebookType', newValue);
              // Clear subtype when type changes
              if (newValue !== 'purchased') {
                onChange('scorebookSubtype', null);
              }
            }}
            className={selectClassName}
          >
            <option value="">Not specified</option>
            {SCOREBOOK_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Conditional: Scorebook subtype for "purchased" */}
        {values.scorebookType === 'purchased' && (
          <div>
            <label htmlFor="scorebook-brand" className={labelClassName}>
              Scorebook brand
            </label>
            <select
              id="scorebook-brand"
              value={values.scorebookSubtype || ''}
              onChange={(e) => {
                const newValue = e.target.value || null;
                onChange('scorebookSubtype', newValue);
              }}
              className={selectClassName}
            >
              <option value="">Not specified</option>
              {PURCHASED_SCOREBOOK_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

export default ScorecardMetadataFields;
